import { Button } from "@/components/ui/button"
import { getLocalizedValue, type Locale } from "@/lib/i18n"
import { urlForImage } from "@/lib/sanity"
import type { CareerSection as CareerSectionData } from "@/types/sanity"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowRight } from "@fortawesome/free-solid-svg-icons"
import { Link } from "react-router"

type CareerSectionProps = {
  section: CareerSectionData
  locale: Locale
}

const defaultButtonText = {
  tr: "İletişime Geçin",
  en: "Get in Touch",
}

function getImageAlt(section: CareerSectionData, locale: Locale): string {
  const alt = section.image?.alt
  if (typeof alt === "string") {
    return alt
  }
  return getLocalizedValue(alt, locale, getLocalizedValue(section.title, locale, ""))
}

export function CareerSection({
  section,
  locale,
}: CareerSectionProps): React.JSX.Element {
  const eyebrow = getLocalizedValue(section.eyebrow, locale, "")
  const title = getLocalizedValue(section.title, locale, "")
  const description = getLocalizedValue(section.description, locale, "")
  const buttonText = getLocalizedValue(
    section.buttonText,
    locale,
    defaultButtonText[locale]
  )
  const configuredButtonLink = section.buttonLink?.trim() ?? ""
  const buttonLink =
    configuredButtonLink.length > 0 ? configuredButtonLink : "/contact"
  const isExternalLink = /^(https?:|mailto:|tel:)/.test(buttonLink)

  return (
    <section className="py-24 bg-linear-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="relative overflow-hidden rounded-2xl shadow-2xl bg-slate-100 aspect-4/3">
            {section.image ? (
              <>
                <img
                  src={urlForImage(section.image)
                    .width(800)
                    .height(600)
                    .fit("crop")
                    .url()}
                  alt={getImageAlt(section, locale)}
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-linear-to-tr from-[#0077B6]/15 to-transparent" />
              </>
            ) : (
              <div className="h-full w-full bg-linear-to-br from-[#0077B6] via-[#1FC1B1] to-[#568F22] flex items-center justify-center">
                <span className="text-5xl font-bold text-white/25">
                  POREMAT
                </span>
              </div>
            )}
          </div>

          <div>
            {eyebrow.length > 0 && (
              <p className="mb-4 text-sm font-semibold tracking-[0.28em] uppercase text-[#0077B6]">
                {eyebrow}
              </p>
            )}
            <h2 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
              <span className="gradient-brand">{title}</span>
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed whitespace-pre-line mb-8 max-w-xl">
              {description}
            </p>
            {isExternalLink ? (
              <Button
                asChild
                size="lg"
                className="group bg-[#0077B6] hover:bg-[#00659B] text-white"
              >
                <a href={buttonLink}>
                  {buttonText}
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform"
                  />
                </a>
              </Button>
            ) : (
              <Button
                asChild
                size="lg"
                className="group bg-[#0077B6] hover:bg-[#00659B] text-white"
              >
                <Link to={buttonLink}>
                  {buttonText}
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform"
                  />
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
